import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { loginApi, getStoredToken } from '../api/loginApi';
import type { LoginRequest, LoginResponse } from '../types/login';

/** Mutation hook for POST /api/eraf/auth/login */
export function useLogin() {
  const navigate = useNavigate();

  return useMutation<LoginResponse, { code?: string; message?: string; traceId?: string }, LoginRequest>({
    mutationFn: (req: LoginRequest) => loginApi.login(req),
    onSuccess: (data, vars) => {
      // R7-031 fix: ensure storage write completes before navigate.
      // rememberMe=true → localStorage (30-day persistence)
      // rememberMe=false → sessionStorage (tab-scoped)
      const storage = vars.rememberMe ? localStorage : sessionStorage;
      try {
        storage.setItem('access_token', data.accessToken);
        storage.setItem('refresh_token', data.refreshToken);
        storage.setItem(
          'refresh_expires_in',
          String(data.refreshExpiresInSeconds)
        );
        storage.setItem(
          'access_expires_in',
          String(data.accessExpiresInSeconds)
        );
      } catch (storageErr) {
        // storage 접근 실패(private 브라우징 제한 등)를 무시하고 navigate 는 진행
        console.warn('[useLogin] storage.setItem failed:', storageErr);
      }
      // R7-031: navigate 는 storage write 직후 동기적으로 호출.
      // React Router v6 navigate 는 동기 call stack 에서 즉시 실행됨.
      navigate('/_index', { replace: true });
    },
  });
}

/** Query hook for GET /api/eraf/auth/me */
export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => loginApi.me(),
    enabled: !!getStoredToken(),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: false,
  });
}

/** Mutation hook for POST /api/eraf/auth/logout */
export function useLogout() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => loginApi.logout(),
    onSettled: () => {
      const keys = ['access_token', 'refresh_token', 'refresh_expires_in', 'access_expires_in'];
      keys.forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
      qc.clear();
      navigate('/login');
    },
  });
}

/** Mutation hook for POST /api/eraf/auth/refresh */
export function useRefresh() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (refreshToken: string) => loginApi.refresh(refreshToken),
    onSuccess: (data) => {
      // Keep existing storage strategy (check localStorage first)
      const usesLocalStorage = !!localStorage.getItem('refresh_token');
      const storage = usesLocalStorage ? localStorage : sessionStorage;
      storage.setItem('access_token', data.accessToken);
      storage.setItem('refresh_token', data.refreshToken);
      storage.setItem('refresh_expires_in', String(data.refreshExpiresInSeconds));
      storage.setItem('access_expires_in', String(data.accessExpiresInSeconds));
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}
